// Simple utility functions without external dependencies
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date)
}

export function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

export function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export function calculateAge(birthDate: Date): number {
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }

  return age
}

export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371 // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180)
  const dLon = (lon2 - lon1) * (Math.PI / 180)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c
  return distance
}

export function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function getSubscriptionFeatures(tier: string) {
  const features = {
    FREE: [
      'Basic park finder',
      'Manual tracking logs',
      'Community reviews',
      'Limited food database',
      '1 playdate request/month',
    ],
    PAW_PLUS: [
      'Real-time park updates',
      'Unlimited tracking logs',
      'Smart AI recommendations',
      'Unlimited playdate requests',
      'Social dashboard',
    ],
    PREMIUM: [
      'AI wellness reports',
      'Vet-ready PDFs',
      'Food purchase integrations',
      'Priority support',
      'Advanced playdate matching',
    ],
    FAMILY: [
      'Track up to 5 dogs',
      'Shared family dashboards',
      'Cross-pet analytics',
      'Premium playdate tools',
      'Multi-dog household features',
    ],
  }

  return features[tier as keyof typeof features] || features.FREE
}

/**
 * Format age bracket to display-friendly label
 * Maps: puppy -> Puppy, young_adult -> Young Adult, adult -> Adult, senior -> Senior
 * Falls back to calculating from ageYears if ageBracket is not provided
 */
export function formatAgeBracket(
  ageBracket?: string | null,
  ageYears?: number | null
): string | null {
  // First, try to use ageBracket if provided
  if (ageBracket) {
    const bracket = ageBracket.toLowerCase().trim()
    if (bracket === 'puppy') return 'Puppy'
    if (bracket === 'young_adult' || bracket === 'young adult') return 'Young Adult'
    if (bracket === 'adult') return 'Adult'
    if (bracket === 'senior') return 'Senior'
  }

  // Fallback: Calculate from ageYears
  if (ageYears !== undefined && ageYears !== null && !isNaN(ageYears)) {
    const age = ageYears
    if (age < 1) return 'Puppy'
    if (age >= 1 && age < 4) return 'Young Adult'
    if (age >= 4 && age < 8) return 'Adult'
    if (age >= 8) return 'Senior'
  }

  return null
}
